import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, ChevronDown, FileSearch, Lock, Search, Star } from "lucide-react";
import { api } from "@/lib/api";
import { TopicCard } from "@/components/TopicCard";
import { Marquee } from "@/components/Marquee";
import { Dashboard } from "@/components/Dashboard";
import { ArticleRow } from "@/components/ArticleRow";
import { InvestigationCTA } from "@/components/InvestigationCTA";
import { HOME, FILTER, TOPIC } from "@/constants/testIds";
import { renderWithRedactions } from "@/components/TextFormatter";
import { displayTitleForTopic } from "@/lib/factAlignment";

const CATEGORIES = [
        { slug: "all", label: "Todos" },
        { slug: "politica", label: "Política" },
        { slug: "media", label: "Media" },
        { slug: "justica", label: "Justiça" },
        { slug: "economico", label: "Económico" },
        { slug: "social", label: "Social" },
        { slug: "historico", label: "Histórico" },
        { slug: "ciencia_saude", label: "Ciência e Saúde" },
        { slug: "religiao", label: "Religião" },
        { slug: "esoterismo", label: "Esoterismo" },
];

const SORTS = [
        { key: "recent", label: "Recentes" },
        { key: "controversy", label: "Mais controverso" },
];

const EDITORIAL_CARD_LIMIT = 6;
const INITIAL_DOSSIER_LIMIT = 12;
const DOSSIER_INCREMENT = 12;
const DOSSIER_PAGE_SIZE = 36;
const DOSSIER_SUMMARY_ENDPOINT = "/topics/summary/page";
const LIVE_ARTICLE_LIMIT = 4;
const EMPTY_TOPIC_PAGE = {
        total: 0,
        limit: DOSSIER_PAGE_SIZE,
        offset: 0,
        has_more: false,
};

const categoryLabel = (slug) =>
        CATEGORIES.find((category) => category.slug === slug)?.label || slug || "Arquivo";

const formatTopicDate = (iso) => {
        if (!iso) return "Data reservada";
        return new Date(iso).toLocaleDateString("pt-PT", {
                day: "2-digit",
                month: "short",
                year: "numeric",
        });
};

const DossierArchiveRow = ({ topic, index }) => {
        const score = topic.controversy_score ?? 0;
        const displayTitle = displayTitleForTopic(topic);
        return (
                <Link
                        to={`/topico/${topic.slug}`}
                        data-testid={TOPIC.card(topic.slug)}
                        className="group grid grid-cols-1 lg:grid-cols-[110px_1fr_130px] gap-4 border-b border-[var(--line)] py-5 hover:bg-[var(--paper)] px-3 -mx-3 transition-colors"
                >
                        <div className="flex lg:flex-col items-center lg:items-start gap-3 lg:gap-2">
                                <span className="line-no">
                                        A.{String(index + 1).padStart(3, "0")}
                                </span>
                                <span className="overline text-[var(--ink-soft)]">
                                        {categoryLabel(topic.category)}
                                </span>
                        </div>
                        <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-3 mb-2 text-[var(--ink-faint)]">
                                        <span className="inline-flex items-center gap-1 font-mono text-[11px]">
                                                <CalendarDays size={12} strokeWidth={1.5} />
                                                {formatTopicDate(topic.created_at)}
                                        </span>
                                        {topic.tags?.slice(0, 3).map((tag) => (
                                                <span
                                                        key={tag}
                                                        className="overline border border-[var(--line)] px-2 py-[2px] text-[var(--ink-soft)]"
                                                >
                                                        {tag}
                                                </span>
                                        ))}
                                </div>
                                <h3 className="font-serif text-2xl md:text-3xl leading-tight text-[var(--ink)] group-hover:text-[var(--controversy)] transition-colors">
                                        <span data-testid={TOPIC.cardTitle(topic.slug)}>
                                                {renderWithRedactions(displayTitle)}
                                        </span>
                                </h3>
                                <p className="mt-2 text-[13px] leading-relaxed text-[var(--ink-soft)] line-clamp-2 font-mono">
                                        {renderWithRedactions(topic.summary)}
                                </p>
                        </div>
                        <div className="flex lg:flex-col items-center lg:items-end justify-between gap-3">
                                <div className="font-mono text-xs text-[var(--ink-soft)]">
                                        Índice{" "}
                                        <span
                                                className="font-bold"
                                                style={{
                                                        color:
                                                                score > 70
                                                                        ? "var(--deception)"
                                                                        : score > 40
                                                                        ? "var(--controversy)"
                                                                        : "var(--truth)",
                                                }}
                                        >
                                                {String(score).padStart(2, "0")}
                                        </span>
                                </div>
                                <ArrowUpRight
                                        size={18}
                                        strokeWidth={1.5}
                                        className="text-[var(--ink-faint)] group-hover:text-[var(--controversy)]"
                                />
                        </div>
                </Link>
        );
};

export default function Home() {
        const [topics, setTopics] = useState([]);
        const [trending, setTrending] = useState([]);
        const [articles, setArticles] = useState([]);
        const [stats, setStats] = useState(null);
        const [category, setCategory] = useState("all");
        const [search, setSearch] = useState("");
        const [sort, setSort] = useState("recent");
        const [visibleDossiers, setVisibleDossiers] = useState(INITIAL_DOSSIER_LIMIT);
        const [loading, setLoading] = useState(true);
        const [loadingMore, setLoadingMore] = useState(false);
        const [topicPage, setTopicPage] = useState(EMPTY_TOPIC_PAGE);

        useEffect(() => {
                let cancelled = false;
                setLoading(true);
                const params = { sort, limit: DOSSIER_PAGE_SIZE, offset: 0 };
                if (category !== "all") params.category = category;
                if (search.trim()) params.search = search.trim();
                api.get(DOSSIER_SUMMARY_ENDPOINT, { params })
                        .then((r) => {
                                if (cancelled) return;
                                const nextTopics = r.data?.items || [];
                                setTopics(nextTopics);
                                setTopicPage({
                                        total: r.data?.total || 0,
                                        limit: r.data?.limit || DOSSIER_PAGE_SIZE,
                                        offset: r.data?.offset || 0,
                                        has_more: Boolean(r.data?.has_more),
                                });
                        })
                        .catch(() => {
                                if (cancelled) return;
                                setTopics([]);
                                setTopicPage(EMPTY_TOPIC_PAGE);
                        })
                        .finally(() => !cancelled && setLoading(false));
                return () => {
                        cancelled = true;
                };
        }, [category, search, sort]);

        useEffect(() => {
                setVisibleDossiers(INITIAL_DOSSIER_LIMIT);
        }, [category, search, sort]);

        useEffect(() => {
                api.get("/topics/trending").then((r) => setTrending(r.data)).catch(() => {});
                api.get("/articles", { params: { limit: 12 } })
                        .then((r) => setArticles(r.data))
                        .catch(() => {});
                api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
        }, []);

        const heroTopic = useMemo(() => {
                const featured = topics.filter((t) => t.is_featured);
                if (featured.length > 0) {
                        return featured.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                }
                return topics.slice().sort((a, b) => b.controversy_score - a.controversy_score)[0];
        }, [topics]);

        const secondaryFeatured = useMemo(() => {
                const featured = topics.filter((t) => t.is_featured);
                if (featured.length > 1) {
                        return featured.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(1, 4);
                }
                return [];
        }, [topics]);

        const visibleTopics = useMemo(
                () => topics.slice(0, visibleDossiers),
                [topics, visibleDossiers]
        );
        const editorialArchiveTopics = useMemo(
                () => visibleTopics.slice(0, EDITORIAL_CARD_LIMIT),
                [visibleTopics]
        );
        const compactArchiveTopics = useMemo(
                () => visibleTopics.slice(EDITORIAL_CARD_LIMIT),
                [visibleTopics]
        );
        const topicTotal = topicPage.total || topics.length;
        const hasMoreDossiers = Boolean(topicPage.has_more);
        const canRevealLoadedDossiers = visibleDossiers < topics.length;
        const canLoadMoreDossiers = canRevealLoadedDossiers || hasMoreDossiers;
        const archiveStatus = topics.length > 0
                ? `${visibleTopics.length} de ${topicTotal} dossiês visíveis.${hasMoreDossiers ? " Há mais arquivo para carregar." : ""}`
                : "Selecione filtros ou procure por tema.";

        const loadMoreDossiers = async () => {
                if (loadingMore) return;
                if (canRevealLoadedDossiers) {
                        setVisibleDossiers((current) =>
                                Math.min(current + DOSSIER_INCREMENT, topics.length)
                        );
                        return;
                }
                if (!hasMoreDossiers) return;

                setLoadingMore(true);
                try {
                        const params = { sort, limit: DOSSIER_PAGE_SIZE, offset: topics.length };
                        if (category !== "all") params.category = category;
                        if (search.trim()) params.search = search.trim();
                        const response = await api.get(DOSSIER_SUMMARY_ENDPOINT, { params });
                        const nextTopics = response.data?.items || [];
                        const loadedCount = topics.length + nextTopics.length;
                        setTopics((current) => [...current, ...nextTopics]);
                        setVisibleDossiers((current) =>
                                Math.min(current + DOSSIER_INCREMENT, loadedCount)
                        );
                        setTopicPage({
                                total: response.data?.total || loadedCount,
                                limit: response.data?.limit || DOSSIER_PAGE_SIZE,
                                offset: response.data?.offset || topics.length,
                                has_more: Boolean(response.data?.has_more),
                        });
                } catch (e) {
                        setTopicPage((current) => ({ ...current, has_more: false }));
                } finally {
                        setLoadingMore(false);
                }
        };

        return (
                <main className="relative">
                        {/* HERO — newspaper masthead */}
                        <section className="border-b border-[var(--line-strong)] relative overflow-hidden">
                                <div className="absolute inset-0 crosshair-bg opacity-30 pointer-events-none" />
                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-12 relative">
                                        <div className="md:col-span-8">
                                                <div className="flex flex-wrap items-center gap-4 mb-8">
                                                        <span className="stamp pulse-class">
                                                                Confidencial · acesso público
                                                        </span>
                                                        <span className="overline text-[var(--ink-faint)]">
                                                                Dossiê PT · arquivo aberto · estritamente analítico
                                                        </span>
                                                </div>
                                                <h1 className="font-serif text-[64px] sm:text-[80px] lg:text-[112px] tracking-tighter leading-[0.9] font-bold text-[var(--ink)]">
                                                        O que se{" "}
                                                        <span className="italic text-[var(--controversy)]">
                                                                diz.
                                                        </span>
                                                        <br />
                                                        <span className="text-[var(--ink-soft)]">
                                                                O que se{" "}
                                                        </span>
                                                        <span className="italic" style={{ color: "var(--deception)" }}>
                                                                esconde.
                                                        </span>
                                                </h1>
                                                <p className="mt-10 max-w-2xl text-[15px] leading-relaxed text-[var(--ink-soft)] font-mono">
                                                        Arquivo independente de controvérsias políticas e
                                                        mediáticas em Portugal. Catalogamos os tópicos
                                                        mais sensíveis, contrastamos factos verificáveis
                                                        com narrativas em circulação, identificamos a
                                                        estratégia de manipulação utilizada. As fontes
                                                        são citadas. Os bastidores deixam de o ser.
                                                </p>
                                                <div className="mt-10 flex flex-wrap gap-3">
                                                        <a
                                                                href="#arquivo"
                                                                data-testid={HOME.heroCta}
                                                                className="overline px-6 py-3 bg-[var(--controversy)] text-[var(--bg-deep)] hover:bg-[var(--ink)] transition-colors inline-flex items-center gap-2"
                                                        >
                                                                <FileSearch
                                                                        size={14}
                                                                        strokeWidth={2}
                                                                />
                                                                Explorar dossiês →
                                                        </a>
                                                        <Link
                                                                to="/noticias"
                                                                className="overline px-6 py-3 border border-[var(--line-strong)] text-[var(--ink)] hover:border-[var(--controversy)] hover:text-[var(--controversy)] transition-colors"
                                                        >
                                                                Dossiês ao vivo
                                                        </Link>
                                                </div>
                                        </div>

                                        <aside className="md:col-span-4 md:border-l border-[var(--line-strong)] md:pl-8 relative">
                                                <div className="overline text-[var(--ink-faint)] mb-4 flex items-center gap-2">
                                                        <Lock size={11} strokeWidth={1.5} /> Em destaque
                                                </div>
                                                {heroTopic ? (
                                                        <Link
                                                                to={`/topico/${heroTopic.slug}`}
                                                                className="block group"
                                                        >
                                                                <span className="stamp-gold stamp inline-flex">
                                                                        Índice {heroTopic.controversy_score}/100
                                                                </span>
                                                                <h2 className="font-serif text-3xl md:text-4xl leading-[1.05] mt-6 link-underline inline group-hover:text-[var(--controversy)] transition-colors">
                                                                        {heroTopic.title}
                                                                </h2>
                                                                <p className="text-[13px] text-[var(--ink-soft)] mt-5 leading-relaxed font-mono">
                                                                        {heroTopic.summary}
                                                                </p>
                                                                <span className="overline mt-6 inline-block text-[var(--controversy)]">
                                                                        → Abrir dossiê
                                                                </span>
                                                        </Link>
                                                ) : (
                                                        <p className="text-sm text-[var(--ink-faint)] font-mono">
                                                                A carregar...
                                                        </p>
                                                )}
                                        </aside>
                                </div>
                        </section>

                        {/* SECONDARY FEATURED */}
                        {secondaryFeatured.length > 0 && (
                                <section className="border-b border-[var(--line-strong)] bg-[var(--paper)]">
                                        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-10">
                                                <div className="overline text-[var(--ink-faint)] mb-6 flex items-center gap-2">
                                                        <Star size={11} strokeWidth={1.5} /> Também em Destaque
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        {secondaryFeatured.map((t) => (
                                                                <TopicCard key={t.id} topic={t} />
                                                        ))}
                                                </div>
                                        </div>
                                </section>
                        )}

                        {/* MARQUEE */}
                        <Marquee items={trending} />

                        {/* Latest articles (live RSS feed) */}
                        {articles.length > 0 && (
                                <section className="border-b border-[var(--line-strong)]">
                                        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-14">
                                                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
                                                        <div>
                                                                <div className="overline text-[var(--ink-faint)] mb-2 flex items-center gap-2">
                                                                        <span
                                                                                className="pulse-class"
                                                                                style={{ color: "var(--deception)" }}
                                                                        >
                                                                                ●
                                                                        </span>
                                                                        Amostra do indexador · fontes portuguesas
                                                                </div>
                                                                <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-none text-[var(--ink)]">
                                                                        Radar das fontes.
                                                                </h2>
                                                                <p className="mt-3 max-w-2xl text-sm text-[var(--ink-soft)] font-mono">
                                                                        Recorte curto do fluxo RSS. A cobertura completa fica em Dossiês ao vivo.
                                                                </p>
                                                        </div>
                                                        <Link
                                                                to="/noticias"
                                                                className="overline text-[var(--ink-soft)] hover:text-[var(--controversy)]"
                                                        >
                                                                Ver todos →
                                                        </Link>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                                                        {articles.slice(0, LIVE_ARTICLE_LIMIT).map((a, i) => (
                                                                <ArticleRow
                                                                        key={a.id}
                                                                        article={a}
                                                                        index={i}
                                                                        compact
                                                                />
                                                        ))}
                                                </div>
                                        </div>
                                </section>
                        )}

                        {/* Dashboard */}
                        <Dashboard stats={stats} />

                        {/* FILTERS */}
                        <section
                                id="arquivo"
                                className="border-t border-b border-[var(--line-strong)] bg-[var(--bg-deep)]"
                        >
                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                                        <div>
                                                <div className="overline text-[var(--ink-faint)] mb-2">
                                                        § Arquivo
                                                </div>
                                                <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-none text-[var(--ink)]">
                                                        Dossiês em arquivo.
                                                </h2>
                                                <p className="mt-3 max-w-xl text-sm text-[var(--ink-soft)] font-mono">
                                                        {archiveStatus}
                                                </p>
                                        </div>

                                        <div className="flex flex-col md:flex-row gap-4 md:items-end">
                                                <div className="relative">
                                                        <Search
                                                                size={14}
                                                                strokeWidth={1.5}
                                                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-faint)]"
                                                        />
                                                        <input
                                                                data-testid={HOME.searchInput}
                                                                value={search}
                                                                onChange={(e) => setSearch(e.target.value)}
                                                                placeholder="Procurar tópico, tag, tema..."
                                                                className="border bg-transparent pl-9 pr-3 py-2 font-mono text-sm w-72 focus:outline-none focus:border-[var(--controversy)] transition-colors"
                                                                style={{ borderColor: "var(--line-strong)" }}
                                                        />
                                                </div>

                                                <div
                                                        data-testid={HOME.sortToggle}
                                                        className="inline-flex border border-[var(--line-strong)]"
                                                >
                                                        {SORTS.map((s) => (
                                                                <button
                                                                        key={s.key}
                                                                        data-testid={`sort-${s.key}`}
                                                                        onClick={() => setSort(s.key)}
                                                                        className={`overline px-3 py-2 transition-colors ${
                                                                                sort === s.key
                                                                                        ? "bg-[var(--controversy)] text-[var(--bg-deep)]"
                                                                                        : "text-[var(--ink-soft)] hover:text-[var(--ink)]"
                                                                        }`}
                                                                >
                                                                        {s.label}
                                                                </button>
                                                        ))}
                                                </div>
                                        </div>
                                </div>

                                <div className="mx-auto max-w-[1400px] px-6 lg:px-12 pb-8 flex flex-wrap gap-2">
                                        {CATEGORIES.map((c) => {
                                                const active = category === c.slug;
                                                return (
                                                        <button
                                                                key={c.slug}
                                                                data-testid={
                                                                        c.slug === "all"
                                                                                ? FILTER.categoryAll
                                                                                : FILTER.category(c.slug)
                                                                }
                                                                onClick={() => setCategory(c.slug)}
                                                                className={`overline px-3 py-2 border transition-colors ${
                                                                        active
                                                                                ? "bg-[var(--controversy)] text-[var(--bg-deep)] border-[var(--controversy)]"
                                                                                : "border-[var(--line)] text-[var(--ink-soft)] hover:border-[var(--ink)] hover:text-[var(--ink)]"
                                                                }`}
                                                        >
                                                                {c.label}
                                                        </button>
                                                );
                                        })}
                                </div>
                        </section>

                        {/* GRID */}
                        <section
                                data-testid={HOME.archiveGrid}
                                className="mx-auto max-w-[1400px] px-6 lg:px-12 py-10"
                        >
                                {loading ? (
                                        <div className="p-12 text-center font-mono text-sm text-[var(--ink-faint)]">
                                                A descodificar arquivo...
                                        </div>
                                ) : topics.length === 0 ? (
                                        <div className="py-16">
                                                {search.trim().length >= 3 ? (
                                                        <InvestigationCTA
                                                                query={search.trim()}
                                                                defaultCategory={
                                                                        category !== "all" ? category : "politica"
                                                                }
                                                        />
                                                ) : (
                                                        <div className="text-center">
                                                                <p className="font-serif text-4xl text-[var(--ink)]">
                                                                        Sem dossiês para este filtro.
                                                                </p>
                                                                <p className="text-[var(--ink-soft)] mt-2 font-mono">
                                                                        Tente outra categoria ou escreva um termo.
                                                                </p>
                                                        </div>
                                                )}
                                        </div>
                                ) : (
                                        <>
                                                <div className="mb-6 flex items-center justify-between gap-4">
                                                        <div>
                                                                <div className="overline text-[var(--ink-faint)] mb-2">
                                                                        Gaveta principal
                                                                </div>
                                                                <p className="text-sm font-mono text-[var(--ink-soft)]">
                                                                        Os primeiros resultados ficam em cartão; o resto segue em modo arquivo.
                                                                </p>
                                                        </div>
                                                        <span className="hidden md:inline-flex overline border border-[var(--line)] px-3 py-2 text-[var(--ink-soft)]">
                                                                {visibleTopics.length} visíveis
                                                        </span>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 rise">
                                                        {editorialArchiveTopics.map((t, i) => (
                                                                <TopicCard key={t.id} topic={t} index={i} />
                                                        ))}
                                                </div>

                                                {compactArchiveTopics.length > 0 && (
                                                        <div className="mt-10 border-t border-[var(--line-strong)] pt-8">
                                                                <div className="mb-3 flex items-center justify-between gap-4">
                                                                        <div className="overline text-[var(--ink-faint)]">
                                                                                Arquivo compacto
                                                                        </div>
                                                                        <div className="font-mono text-xs text-[var(--ink-faint)]">
                                                                                resultados {EDITORIAL_CARD_LIMIT + 1}–
                                                                                {visibleTopics.length}
                                                                        </div>
                                                                </div>
                                                                <div className="border-y border-[var(--line-strong)]">
                                                                        {compactArchiveTopics.map((t, i) => (
                                                                                <DossierArchiveRow
                                                                                        key={t.id}
                                                                                        topic={t}
                                                                                        index={i + EDITORIAL_CARD_LIMIT}
                                                                                />
                                                                        ))}
                                                                </div>
                                                        </div>
                                                )}

                                                {canLoadMoreDossiers && (
                                                        <div className="mt-10 flex justify-center">
                                                                <button
                                                                        type="button"
                                                                        onClick={loadMoreDossiers}
                                                                        disabled={loadingMore}
                                                                        className="overline px-5 py-3 border border-[var(--line-strong)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--bg-deep)] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                                                                >
                                                                        <ChevronDown size={14} strokeWidth={1.5} />
                                                                        {loadingMore ? "A carregar..." : "Carregar mais"}
                                                                        <span className="text-[10px] text-current opacity-60">
                                                                                {visibleTopics.length}/{topicTotal}
                                                                        </span>
                                                                </button>
                                                        </div>
                                                )}
                                        </>
                                )}
                        </section>
                </main>
        );
}
